// TODO figure out what to do with this component RE SuperSelect
import React from 'react'
import { GroupedVirtuoso, Virtuoso } from 'react-virtuoso'
import {
  defaultMenuHeight,
  optionMinHeight,
  StyledOptions,
} from './NextMenu.styles'
import type { NextMenuProps } from './NextMenu.types'
import { NextMenuGroup, NextMenuItem } from './NextMenuItem'

/**
 * Count items in groups by id received from getItemGroupId
 *
 * @param items_
 * @param groups_
 * @param getItemGroupId
 * @return number[]
 * */
export const getGroupCounts = (
  items_: any[],
  groups_: any[],
  getItemGroupId: (item: any) => string | number
): number[] => {
  return groups_.map((group) => {
    const groupedItems = items_.filter(
      (item) => getItemGroupId(item) === group.id
    )
    return groupedItems.length
  })
}

/**
 * Get the height of the item list, used to make menu grow to size of item list
 * (or to a max height if applied by implementer) with a minimum of 32px
 * @param height;
 * @returns number
 */
function calculateHeight(height: number) {
  return Math.max(height, optionMinHeight)
}

const Options = React.forwardRef<HTMLDivElement, any>((props, ref) => {
  return <StyledOptions ref={ref} {...props} />
})

const flatComponents = { Item: NextMenuItem, List: Options }

const groupedComponents = {
  Group: NextMenuGroup,
  Item: NextMenuItem,
  List: Options,
}

/**
 Menus are used in conjunction with components that contain dropdowns.
 For example, multi select, single select, and dropdown.

 @since 11.1.1
 @see [Storybook](https://stories.core.procore.com/?path=/story/core-react_coverage-nextmenu--with-groups)
 @see [Design Guidelines](https://design.procore.com/menu)
 */
export const NextMenu = React.forwardRef<
  React.ReactElement,
  NextMenuProps<any, any>
>(({ style, ...props }, menuRef) => {
  const [adjustedHeight, setAdjustedHeight] = React.useState(defaultMenuHeight)

  const sharedMenuConfiguration = {
    ref: menuRef,
    style: {
      height: adjustedHeight,
      minHeight: optionMinHeight,
      ...style,
    },
    totalListHeightChanged: (height: number) =>
      setAdjustedHeight(calculateHeight(height)),
  }

  if (!props.groups?.length) {
    return <NextMenuFlat {...sharedMenuConfiguration} {...props} />
  }
  return <NextMenuGrouped {...sharedMenuConfiguration} {...props} />
})

/**
 * Flat version of menu list
 * */
export const NextMenuFlat = React.forwardRef<
  React.ReactElement,
  NextMenuProps<any, any> & { totalListHeightChanged: (height: number) => void }
>(
  (
    {
      items = [],
      overscan = 20,
      style,
      totalListHeightChanged,
      onEndReached = () => {},
      components = {},
      getItemLabel = (item: any) => item?.label,
    },
    menuRef
  ) => {
    return (
      <Virtuoso
        ref={menuRef as any}
        components={{ ...flatComponents, ...components } as any}
        context={{ items, getItemLabel }}
        data={items}
        overscan={overscan}
        style={style}
        totalListHeightChanged={totalListHeightChanged}
        endReached={onEndReached}
      />
    )
  }
)

/**
 * Grouped version of menu list
 * Provide getItemGroupId to group items
 * @see getGroupCounts
 * */
export const NextMenuGrouped = React.forwardRef<
  React.ReactElement,
  NextMenuProps<any, any> & { totalListHeightChanged: (height: number) => void }
>((props, menuRef) => {
  const {
    children,
    components = {},
    items = [],
    groups = [],
    getItemGroupId = () => -1,
    overscan = 20,
    onSelect: onSelect_,
    onEndReached: onEndReached_ = () => {},
    style,
    totalListHeightChanged,
  } = props

  const groupCounts = getGroupCounts(items, groups, getItemGroupId)

  return (
    <GroupedVirtuoso
      ref={menuRef as any}
      style={style}
      components={{ ...groupedComponents, ...components } as any}
      context={{ items }}
      groupCounts={groupCounts}
      overscan={overscan}
      endReached={onEndReached_}
      totalListHeightChanged={totalListHeightChanged}
    />
  )
})
